const decode = require('unescape')

export default class Vehicle {
  public id: number
  public modificationDate: number
  public customerId: number
  public properties: VehicleProperties
  public images: string[]
  public videos: Video[]
  public entryDate: number

  constructor(vehicle: any) {
    this.id = vehicle.id
    this.modificationDate = vehicle.modificationDate ? vehicle.modificationDate : null
    this.customerId = vehicle.customerId ? vehicle.customerId : null
    this.properties = new VehicleProperties(vehicle.properties)
    this.images = vehicle.images
    this.entryDate = vehicle.entry_date ? vehicle.entry_date : 0

    this.videos = []

    // Add videos
    if (
      vehicle.properties.videos &&
      vehicle.properties.videos.length !== undefined &&
      vehicle.properties.videos.length > 0
    ) {
      vehicle.properties.videos.forEach((video: any) => {
        if (video.source && video.code) {
          this.videos.push(new Video(video.source, video.code))
        }
      })
    }
  }
}

class VehicleProperties {
  public license: string
  public brand: string
  public model: string
  public type: string
  public vehicleType: string
  public addition: string
  public body: string
  public numberDoors: number
  public mileage: number
  public mileageUnit: string
  public fuelType: string
  public transmission: string
  public btwMarge: string
  public color: string
  public year: number
  public ageInMonths: number
  public price: number
  public priceInclTax: boolean
  public priceFinanceAmount: number
  public pricePrivateLeaseAmount: number
  public mass: string
  public towingWeight: string
  public numberCilinders: number
  public engineCapacity: string
  public enginePowerKW: string
  public enginePowerPK: string
  public numberSeats: string
  public lining: string
  public averageFuelConsumption: string
  public emissionClass: string
  public energyLabel: string
  public description: string
  public sold: boolean
  public expected: boolean
  public reserved: boolean
  public maintenanceBook: string
  public warranty: {
    nap: boolean
    carpass: boolean
    autotrust: boolean
    vakgarant: boolean
    vweOccasionGarant: boolean
    cargarantie: boolean
    honderdProcentOnderhouden: boolean
    bovag40puntencheck: boolean
    bovagAfleverbeurt: boolean
    bovagGarantie6: boolean
    bovagGarantie12: boolean
    bovagGarantie24: boolean
  }

  public size: {
    length: number
    width: number
    height: number
  }

  public quarterlyRoadTax: {
    min: number
    max: number
  }

  public deliverOptions: DeliverOptions[]
  public options: string[]

  constructor(vehicle: any) {
    this.license = vehicle.license ? vehicle.license : null
    this.brand = vehicle.brand ? vehicle.brand : null
    this.model = vehicle.model ? decode(vehicle.model) : null
    this.type = vehicle.type ? decode(vehicle.type) : null
    this.vehicleType = vehicle.vehicleType ? vehicle.vehicleType : null
    this.addition = vehicle.addition ? decode(vehicle.addition) : null
    this.body = vehicle.body ? vehicle.body : null
    this.numberDoors = vehicle.numberDoors ? vehicle.numberDoors : null
    this.mileage = vehicle.mileage ? vehicle.mileage : null
    this.mileageUnit = vehicle.mileageUnit ? vehicle.mileageUnit : null
    this.fuelType = vehicle.fuelType ? vehicle.fuelType : null
    this.transmission = vehicle.transmission ? vehicle.transmission : null
    this.btwMarge = vehicle.btwMarge ? vehicle.btwMarge : null
    this.color = vehicle.color ? vehicle.color : null
    this.year = vehicle.year ? vehicle.year : null
    this.ageInMonths = vehicle.datum_deel_1 ? calculateVehicleAgeInMonths(vehicle.datum_deel_1) : null
    this.price = vehicle.price ? vehicle.price : vehicle.priceExc ? vehicle.priceExc : null
    this.priceInclTax = vehicle.priceInclTax ? vehicle.priceInclTax : false
    this.priceFinanceAmount = vehicle.financeAmount ? vehicle.financeAmount : null
    this.pricePrivateLeaseAmount = vehicle.privateLeaseAmount ? vehicle.privateLeaseAmount : null
    this.mass = vehicle.mass ? vehicle.mass : null
    this.towingWeight = vehicle.towingWeight ? vehicle.towingWeight : null
    this.numberCilinders = vehicle.numberCilinders ? vehicle.numberCilinders : null
    this.engineCapacity = vehicle.engineCapacity ? vehicle.engineCapacity : null
    this.enginePowerKW = vehicle.enginePowerKW ? vehicle.enginePowerKW : null
    this.enginePowerPK = vehicle.enginePowerPK ? vehicle.enginePowerPK : null
    this.numberSeats = vehicle.numberSeats ? vehicle.numberSeats : null
    this.lining = vehicle.lining ? vehicle.lining : null
    this.averageFuelConsumption = vehicle.averageFuelConsumption ? vehicle.averageFuelConsumption : null
    this.emissionClass = vehicle.emissionClass ? vehicle.emissionClass : null
    this.energyLabel = vehicle.energyLabel ? vehicle.energyLabel : null
    this.description = vehicle.mainDescription
      ? decode(vehicle.mainDescription)
      : vehicle.description
      ? decode(vehicle.description)
      : null
    this.sold = vehicle.sold ? vehicle.sold : false
    this.expected = vehicle.expected ? vehicle.expected : false
    this.reserved = vehicle.reserved ? vehicle.reserved : false
    this.maintenanceBook = vehicle.maintenanceBook ? vehicle.maintenanceBook : null
    this.warranty = {
      nap: vehicle.warranty.nap ? vehicle.warranty.nap : false,
      carpass: vehicle.warranty.carpass ? vehicle.warranty.carpass : false,
      autotrust: getWarranty(vehicle, 'autotrust'),
      vakgarant: getWarranty(vehicle, 'vakgarantPremium'),
      vweOccasionGarant: getWarranty(vehicle, 'vweOccasionGarantPlan'),
      cargarantie: getWarranty(vehicle, 'carGarantie'),
      honderdProcentOnderhouden: vehicle.warranty.honderdProcentOnderhouden
        ? vehicle.warranty.honderdProcentOnderhouden
        : false,
      bovag40puntencheck: getWarranty(vehicle, 'bovag40puntencheck'),
      bovagAfleverbeurt: getWarranty(vehicle, 'bovagAfleverbeurt'),
      bovagGarantie6: getWarranty(vehicle, 'bovagGarantie6'),
      bovagGarantie12: getWarranty(vehicle, 'bovagGarantie12'),
      bovagGarantie24: getWarranty(vehicle, 'bovagGarantie24'),
    }
    this.size = {
      length: vehicle.size.length ? vehicle.size.length : null,
      width: vehicle.size.width ? vehicle.size.width : null,
      height: vehicle.size.height ? vehicle.size.height : null,
    }
    this.quarterlyRoadTax = {
      min: vehicle.quarterlyRoadTax && vehicle.quarterlyRoadTax.min ? vehicle.quarterlyRoadTax.min : null,
      max: vehicle.quarterlyRoadTax && vehicle.quarterlyRoadTax.max ? vehicle.quarterlyRoadTax.max : null,
    }
    this.options = vehicle.altOptions ? vehicle.altOptions : []

    const deliverOptions: DeliverOptions[] = []
    if (vehicle.deliverOptions && vehicle.deliverOptions.length !== undefined && vehicle.deliverOptions.length > 0) {
      /* deliverOptions is an array of options */
      vehicle.deliverOptions.forEach((deliverOption: any) => {
        deliverOptions.push(new DeliverOptions(deliverOption))
      })
    } else if (vehicle.deliverOptions.name) {
      /* deliverOptions is an object of a single option */
      deliverOptions.push(new DeliverOptions(vehicle.deliverOptions))
    }
    this.deliverOptions = deliverOptions
  }
}

class Video {
  public source: string
  public id: string

  constructor(source: string, id: string) {
    this.source = source
    this.id = id
  }
}
class DeliverOptions {
  public name: string
  public description: string
  public items: string[]
  public priceEx: number
  public priceIn: number
  public priceExRel: number
  public priceInRel: number
  public accessories: string
  public apkAtDelivery: boolean
  public warrantyComments: string
  public warranty: {
    brand: boolean
    bovag: boolean
    bovagMonths: number
    bovag40PuntenCheck: boolean
    bovagAfleverbeurt: boolean
    vetos: boolean
    pcaTopOccasion: boolean
    boschCarServiceBestCarSelection: boolean
    vakgarantPremium: boolean
    autotrust: boolean
    vweOccasionGarantPlan: boolean
    carGarantie: boolean
    mbsg: boolean
    dealer: boolean
    additional: boolean
    notes: string
  }

  constructor(deliverOption: any) {
    this.name = deliverOption.name ? deliverOption.name : null
    this.description = deliverOption.description ? decode(deliverOption.description) : null
    this.priceEx = deliverOption.priceEx ? deliverOption.priceEx : null
    this.priceIn = deliverOption.priceIn ? deliverOption.priceIn : null
    this.priceExRel = deliverOption.priceExRel ? deliverOption.priceExRel : null
    this.priceInRel = deliverOption.priceInRel ? deliverOption.priceInRel : null
    this.accessories = deliverOption.accessories ? deliverOption.accessories : null
    this.apkAtDelivery = deliverOption.apkAtDelivery ? deliverOption.apkAtDelivery : null
    this.warrantyComments = deliverOption.warrantyComments ? deliverOption.warrantyComments : null
    this.warranty = {
      brand: deliverOption.warranty.brand ? deliverOption.warranty.brand : false,
      bovag: deliverOption.warranty.bovag ? deliverOption.warranty.bovag : false,
      bovagMonths: deliverOption.warranty.bovagMonths ? deliverOption.warranty.bovagMonths : 0,
      bovag40PuntenCheck: deliverOption.warranty.bovag40PuntenCheck ? deliverOption.warranty.bovag40PuntenCheck : false,
      bovagAfleverbeurt: deliverOption.warranty.bovagAfleverbeurt ? deliverOption.warranty.bovagAfleverbeurt : false,
      vetos: deliverOption.warranty.vetos ? deliverOption.warranty.vetos : false,
      pcaTopOccasion: deliverOption.warranty.pcaTopOccasion ? deliverOption.warranty.pcaTopOccasion : false,
      boschCarServiceBestCarSelection: deliverOption.warranty.boschCarServiceBestCarSelection
        ? deliverOption.warranty.boschCarServiceBestCarSelection
        : false,
      vakgarantPremium: deliverOption.warranty.vakgarantPremium ? deliverOption.warranty.vakgarantPremium : false,
      autotrust: deliverOption.warranty.autotrust ? deliverOption.warranty.autotrust : false,
      vweOccasionGarantPlan: deliverOption.warranty.vweOccasionGarantPlan
        ? deliverOption.warranty.vweOccasionGarantPlan
        : false,
      carGarantie: deliverOption.warranty.carGarantie ? deliverOption.warranty.carGarantie : false,
      mbsg: deliverOption.warranty.mbsg ? deliverOption.warranty.mbsg : false,
      dealer: deliverOption.warranty.dealer ? deliverOption.warranty.dealer : false,
      additional: deliverOption.warranty.additional ? deliverOption.warranty.additional : false,
      notes: deliverOption.warranty.notes ? deliverOption.warranty.notes : null,
    }

    // Add items
    this.items = []
    if (deliverOption.items && typeof deliverOption.items === 'string') {
      this.items.push(decode(deliverOption.items))
    } else {
      for (const item of deliverOption.items) {
        this.items.push(decode(item))
      }
    }
  }
}

/**
 * Function to check if the warranty is set in one of the deliver options (afleverpakketten)
 */
function getWarranty(vehicle: any, warranty: string) {
  const isBovagWarranty =
    warranty === 'bovagGarantie6' || warranty === 'bovagGarantie12' || warranty === 'bovagGarantie24'
  const warrantyKey = isBovagWarranty ? 'bovag' : warranty

  // Search in deliver options
  if (vehicle.deliverOptions && vehicle.deliverOptions.length > 0) {
    /* deliverOptions is an array of options */
    for (const option of vehicle.deliverOptions) {
      if (option.warranty[warrantyKey] && option.warranty[warrantyKey] === true) {
        if (!isBovagWarranty) {
          return true
        } else {
          const bovagCheckMonths = warranty.match(/\d+/g)
          if (bovagCheckMonths && option.warranty.bovagMonths === bovagCheckMonths[0]) {
            return true
          }
        }
      }
    }
  }

  // Search in warranty
  if (vehicle.warranty && vehicle.warranty[warrantyKey]) {
    if (isBovagWarranty) {
      const bovagCheckMonths = warranty.match(/\d+/g)
      if (bovagCheckMonths && vehicle.warranty[warrantyKey] === bovagCheckMonths[0]) {
        return true
      }
    }

    if (vehicle.warranty[warrantyKey] === true) {
      return true
    }
  }

  // Warranty was not found
  return false
}

/**
 * Function to calculate vehicle age in months based on datum_deel_1
 */
function calculateVehicleAgeInMonths(date: string): any {
    const [, month, year] = date.split('-').map(Number)
    const today = new Date()

    return (
        (today.getFullYear() - year) * 12 +
        (today.getMonth() - (month - 1))
    );
}